import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Send, Search } from "lucide-react";

const API = process.env.REACT_APP_API_URL;

const PushNotificationsPanel = () => {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [titulo, setTitulo] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [ruta, setRuta] = useState("");
  const [search, setSearch] = useState("");
  const [empresaId, setEmpresaId] = useState("");
  const [empresas, setEmpresas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [seleccionados, setSeleccionados] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    axios
      .get(`${API}/admin/empresas`, { headers })
      .then((res) => setEmpresas(res.data))
      .catch(() => toast.error("No se pudieron cargar las empresas"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (empresaId) params.empresaId = empresaId;

    const timeout = setTimeout(() => {
      axios
        .get(`${API}/admin/notificaciones/usuarios`, { headers, params })
        .then((res) => setUsuarios(res.data))
        .catch(() => toast.error("No se pudieron cargar los usuarios"));
    }, 300);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, empresaId]);

  const cargarHistorial = (p = 1) => {
    axios
      .get(`${API}/admin/notificaciones`, { headers, params: { page: p } })
      .then((res) => {
        setHistorial(res.data.data);
        setPage(res.data.page);
        setTotalPages(res.data.totalPages);
      })
      .catch(() => toast.error("No se pudo cargar el historial"));
  };

  useEffect(() => {
    cargarHistorial(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSeleccionado = (id) => {
    setSeleccionados((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const seleccionarTodosFiltrados = () => {
    setSeleccionados((prev) => [...new Set([...prev, ...usuarios.map((u) => u._id)])]);
  };

  const rutaValida = useMemo(() => ruta.trim().startsWith("/"), [ruta]);

  const enviar = async () => {
    if (!titulo.trim() || !mensaje.trim()) {
      toast.error("Título y mensaje son obligatorios");
      return;
    }
    if (!rutaValida) {
      toast.error("La ruta debe empezar con /");
      return;
    }
    if (seleccionados.length === 0) {
      toast.error("Selecciona al menos un destinatario");
      return;
    }

    setEnviando(true);
    try {
      await axios.post(
        `${API}/admin/notificaciones`,
        { titulo, mensaje, ruta: ruta.trim(), userIds: seleccionados },
        { headers },
      );
      toast.success("Notificación enviada");
      setTitulo("");
      setMensaje("");
      setRuta("");
      setSeleccionados([]);
      cargarHistorial(1);
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al enviar la notificación");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <h1 className="text-xl font-semibold text-gray-800 mb-6">
        Enviar notificación
      </h1>

      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4 mb-8">
        <input
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          placeholder="Título"
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
        />
        <textarea
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          placeholder="Mensaje"
          rows={3}
          value={mensaje}
          onChange={(e) => setMensaje(e.target.value)}
        />
        <input
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          placeholder="Ruta interna (ej: /equipos/123)"
          value={ruta}
          onChange={(e) => setRuta(e.target.value)}
        />

        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm"
              placeholder="Buscar por nombre o email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            value={empresaId}
            onChange={(e) => setEmpresaId(e.target.value)}
          >
            <option value="">Todas las empresas</option>
            {empresas.map((emp) => (
              <option key={emp._id} value={emp._id}>
                {emp.razon_social}
              </option>
            ))}
          </select>
        </div>

        <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
          {usuarios.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">
              Sin usuarios con notificaciones activas para este filtro
            </p>
          ) : (
            usuarios.map((u) => (
              <label
                key={u._id}
                className="flex items-center gap-3 px-4 py-2 border-b border-gray-100 last:border-0 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={seleccionados.includes(u._id)}
                  onChange={() => toggleSeleccionado(u._id)}
                />
                <div className="min-w-0">
                  <p className="text-sm text-gray-800 truncate">{u.name}</p>
                  <p className="text-xs text-gray-500 truncate">{u.email}</p>
                </div>
              </label>
            ))
          )}
        </div>

        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={seleccionarTodosFiltrados}
            className="text-xs text-blue-600 hover:underline"
          >
            Seleccionar todos los filtrados ({usuarios.length})
          </button>
          <span className="text-xs text-gray-500">
            {seleccionados.length} seleccionado(s)
          </span>
        </div>

        <button
          onClick={enviar}
          disabled={enviando}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg"
        >
          <Send className="h-4 w-4" />
          {enviando ? "Enviando..." : "Enviar notificación"}
        </button>
      </div>

      <h2 className="text-lg font-semibold text-gray-800 mb-4">Historial</h2>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-2">Mensaje</th>
              <th className="text-left px-4 py-2">Ruta</th>
              <th className="text-left px-4 py-2">Fecha</th>
              <th className="text-left px-4 py-2">Destinatarios</th>
            </tr>
          </thead>
          <tbody>
            {historial.map((n) => (
              <tr key={n._id} className="border-t border-gray-100">
                <td className="px-4 py-2">
                  <p className="font-medium text-gray-800">{n.titulo}</p>
                  <p className="text-gray-500 text-xs truncate max-w-xs">
                    {n.mensaje}
                  </p>
                </td>
                <td className="px-4 py-2 text-gray-600">{n.ruta}</td>
                <td className="px-4 py-2 text-gray-600">
                  {new Date(n.createdAt).toLocaleString("es-CO")}
                </td>
                <td className="px-4 py-2 text-gray-600">{n.destinatarios}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => cargarHistorial(p)}
              className={`px-3 py-1 rounded-lg text-sm ${
                p === page ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      )}
    </>
  );
};

export default PushNotificationsPanel;
